import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Matter } from "../models/Matter";
import { MatterService } from "../services/MatterService";
import { useAuth } from "./AuthContext";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { User } from "../models/User";
import { CreateMatterForm } from "../models/CreateMatterForm";
import { Participant } from "../models/Participant";
import { CourtDetails } from "../models/CourtDetails";
import { RelationshipDetails } from "../models/RelationshipDetails";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface MatterContextProps {
  users: User[];
  currentMatter?: Matter | undefined;
  currentMatterId?: string | null;
  partiesInvolved: Participant[];
  setCurrentMatterId: (matterId: string | null) => void;
  setSelectedMatter: (matterId: string) => void;
  matters: Matter[];
  selectedMatter: Matter | null;
  createMatterD: (name: string) => Promise<string>;
  createMatter: (matterData: CreateMatterForm) => Promise<string>;
  updateMatter: (
    matterId: string,
    matterData: Partial<Omit<Matter, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;

  updateCourtDetails: (
    matterId: string,
    courtDetails: Partial<CourtDetails>,
  ) => Promise<void>;
  updateRelationshipDetails: (
    matterId: string,
    relationshipDetails: Partial<RelationshipDetails>,
  ) => Promise<void>;

  deleteMatterById: (matterId: string) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const MatterContext = createContext<MatterContextProps | undefined>(undefined);

export const useMatter = (): MatterContextProps => {
  const context = useContext(MatterContext);
  if (!context)
    throw new Error("useMatter must be used within a MatterProvider");
  return context;
};

export const MatterProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [currentMatterId, setCurrentMatterId] = useState<string | null>(
    localStorage.getItem("currentMatterId"),
  );
  const [matters, setMatters] = useState<Matter[]>([]);
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  // Fetch full matter on selection
  useEffect(() => {
    if (!currentMatterId) return;
    localStorage.setItem("currentMatterId", currentMatterId);
    const fetchMatter = async () => {
      const matterData = await MatterService.getMatter(currentMatterId);
      setSelectedMatter(matterData);
    };
    fetchMatter();
  }, [currentMatterId]);

  useEffect(() => {
    fetchMatters(); // Initial fetch of matters when the component mounts
  }, [user]); // Empty dependency array to run only once on mount

  const fetchMatters = async () => {
    try {
      const allMatters = await MatterService.getAllMatters();
      setMatters(allMatters);
      if (matters.length === 1) {
        setCurrentMatterId(allMatters[0]._id);
      }
      setLoadings((prev) => ({ ...prev, list: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
    }
  };

  useSocketEventByIds("matters:changed", ["*"], () => {
    fetchMatters();
  });

  const createMatterD = async (name: string): Promise<string> => {
    const newMatterId = await MatterService.createMatterD(name);
    setCurrentMatterId(newMatterId);
    // Refresh matters list after creating a new matter
    await fetchMatters();
    return newMatterId;
  };

  const createMatter = async (
    matterData: CreateMatterForm,
  ): Promise<string> => {
    const newMatterId = await MatterService.createMatter(matterData);
    return newMatterId;
  };

  const updateMatter = async (
    matterId: string,
    updatedData: Partial<
      Omit<Matter, "_id" | "createdAt" | "updatedAt" | "createdById">
    >,
  ) => {
    await MatterService.updateMatter(matterId, updatedData);
  };

  const updateCourtDetails = async (
    matterId: string,
    courtDetails: Partial<CourtDetails>,
  ) => {
    await MatterService.updateCourtDetails(matterId, courtDetails);
  };

  const updateRelationshipDetails = async (
    matterId: string,
    relationshipDetails: Partial<RelationshipDetails>,
  ) => {
    await MatterService.updateRelationshipDetails(
      matterId,
      relationshipDetails,
    );
  };

  const updateParties = async (
    matterId: string,
    relationshipDetails: Partial<{
      me?: Participant["_id"];
      opponent?: Participant["_id"];
      kids?: Participant["_id"][];
    }>,
  ) => {
    await MatterService.updateParties(matterId, relationshipDetails);
    // Refresh selected matter to reflect changes
    if (selectedMatter?._id === matterId) {
      await setSelectedMatterById(matterId);
    }
  };

  const deleteMatterById = async (matterId: string) => {
    await MatterService.deleteMatter(matterId);
  };

  const setSelectedMatterById = async (matterId: string) => {
    const matterData = await MatterService.getMatter(matterId);
    setSelectedMatter(matterData);
    setLoadings((prev) => ({ ...prev, details: false }));
  };

  const currentMatter =
    matters.find((c) => c._id === currentMatterId) || undefined;

  const partiesInvolved = [selectedMatter?.me, selectedMatter?.opponent].filter(
    (p): p is Participant => !!p,
  );

  return (
    <MatterContext.Provider
      value={{
        currentMatterId,
        currentMatter,
        users: [],
        partiesInvolved,
        matters,
        selectedMatter,
        createMatter,
        createMatterD,
        updateMatter,
        updateCourtDetails,
        updateRelationshipDetails,
        deleteMatterById,
        setCurrentMatterId,
        setSelectedMatter: setSelectedMatterById,
        loading: loadings,
        error: errors,
      }}
    >
      {children}
    </MatterContext.Provider>
  );
};
